import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import UserManagement from './UserManagement';
import CashInjection from './CashInjection';
import TransactionStatement from '../TransactionStatement';
import CurrencyConversion from '../CurrencyConversion';
import Dashboard from './Dashboard';
import Promotions from './Promotions';
import FundApprovals from './FundApprovals';
import FairPlay from './FairPlay';
import CreateTestUser from '../CreateTestUser';
import { canCreateTestUsers } from '../../config/environment';
import './AdminDashboard.css';
import ChangePasswordModal from '../common/ChangePasswordModal';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:5001'
    : (typeof window !== 'undefined' ? window.location.origin : ''));


// Map each admin dashboard tab to its required capability (Phase 3).
const TAB_PERMISSIONS = {
  'dashboard': ['dashboard'],
  'transactions': ['transactions'],
  'users': ['users'],
  'cash-injection': ['cash.initiate'],
  'currency-conversion': ['currency'],
  'approvals': ['cash.approve1', 'cash.approve_final'],
  'promotions': ['promotions'],
  'test-users': ['test_users'],
  'fair-play': ['dashboard', 'users', 'transactions'],
};

const canAccess = (permissions, tab) => {
  if (!Array.isArray(permissions)) return false;
  if (permissions.includes('full')) return true;
  const required = TAB_PERMISSIONS[tab] || [];
  return required.some((cap) => permissions.includes(cap));
};

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [walletBalances, setWalletBalances] = useState({
    usd: 0,
    inr: 0,
    local: 0
  });
  const [commissionBalance, setCommissionBalance] = useState(0);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [countryInfo, setCountryInfo] = useState({
    country_name: 'Unknown',
    country_code: '',
    local_currency_code: 'USD',
    local_currency_name: 'US Dollar',
    local_currency_symbol: '$'
  });
  const { currentUser, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Permissions for tab visibility (Phase 3)
  const userPermissions =
    (currentUser && currentUser.permissions) ||
    (user && user.permissions) ||
    ['full'];
  const canAccessTab = (tab) => canAccess(userPermissions, tab);

  // Helper function to safely parse numbers
  const safeParseNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  const fetchAdminProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.admin) {
          setUser(data.admin);
          
          // Update country info from admin data
          if (data.admin.country_name || data.admin.currency_code) {
            setCountryInfo(prev => ({
              ...prev,
              country_name: data.admin.country_name || 'Unknown',
              country_code: data.admin.country_code || '',
              local_currency_code: data.admin.currency_code || 'USD',
              local_currency_name: data.admin.currency_name || 'US Dollar',
              local_currency_symbol: data.admin.currency_symbol || '$'
            }));
          }
          
          // Update wallet balances from admin data
          setWalletBalances({
            usd: safeParseNumber(data.admin.wallet_balance_usd),
            inr: safeParseNumber(data.admin.wallet_balance_inr),
            local: safeParseNumber(data.admin.wallet_balance_local || 0)
          });
          
          return data.admin;
        }
      }
    } catch (error) {
      console.error('Error fetching admin profile:', error);
    }
    return null;
  };

  const fetchDashboardSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/dashboard/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          // Update wallet balances
          setWalletBalances(prev => ({
            usd: safeParseNumber(data.data.balance?.usd) || prev.usd,
            inr: safeParseNumber(data.data.balance?.inr) || prev.inr,
            local: safeParseNumber(data.data.balance?.local) || prev.local
          }));
          
          // Update country info if available
          if (data.data.currency_info) {
            setCountryInfo(prev => ({
              ...prev,
              country_name: data.data.currency_info.country_name || prev.country_name,
              country_code: data.data.currency_info.country_code || prev.country_code,
              local_currency_code: data.data.currency_info.local_currency_code || prev.local_currency_code,
              local_currency_name: data.data.currency_info.local_currency_name || prev.local_currency_name,
              local_currency_symbol: data.data.currency_info.local_currency_symbol || prev.local_currency_symbol
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
    }
  };

  const fetchAdminWallet = async () => {
    try {
      // Fetch both profile and dashboard summary
      const token = localStorage.getItem('token');
      
      const [profileResponse, dashboardResponse] = await Promise.all([
        fetch(`${API_BASE}/api/admin/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch(`${API_BASE}/api/admin/dashboard/summary`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      ]);
      
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        if (profileData.success && profileData.admin) {
          setUser(profileData.admin);
          
          // Update country info from admin data
          if (profileData.admin.country_name || profileData.admin.currency_code) {
            setCountryInfo(prev => ({
              ...prev,
              country_name: profileData.admin.country_name || prev.country_name,
              country_code: profileData.admin.country_code || prev.country_code,
              local_currency_code: profileData.admin.currency_code || prev.local_currency_code,
              local_currency_name: profileData.admin.currency_name || prev.local_currency_name,
              local_currency_symbol: profileData.admin.currency_symbol || prev.local_currency_symbol
            }));
          }
        }
      }
      
      if (dashboardResponse.ok) {
        const dashboardData = await dashboardResponse.json();
        if (dashboardData.success && dashboardData.data) {
          // Update wallet balances from dashboard (more accurate)
          setWalletBalances({
            usd: safeParseNumber(dashboardData.data.balance?.usd) || 0,
            inr: safeParseNumber(dashboardData.data.balance?.inr) || 0,
            local: safeParseNumber(dashboardData.data.balance?.local) || 0
          });
          
          // Update country info if available from dashboard
          if (dashboardData.data.currency_info) {
            setCountryInfo(prev => ({
              ...prev,
              country_name: dashboardData.data.currency_info.country_name || prev.country_name,
              local_currency_code: dashboardData.data.currency_info.local_currency_code || prev.local_currency_code,
              local_currency_name: dashboardData.data.currency_info.local_currency_name || prev.local_currency_name,
              local_currency_symbol: dashboardData.data.currency_info.local_currency_symbol || prev.local_currency_symbol
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching admin wallet:', error);
    }
  };

  const fetchCommissionWallet = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/commission-wallet`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) setCommissionBalance(data.data.balance);
      }
    } catch (e) { /* non-fatal */ }
  };

  useEffect(() => {
    // Check if user is admin and authenticated
    const checkAdminAccess = async () => {
      const userData = currentUser || JSON.parse(localStorage.getItem('user'));
      
      console.log('Admin Dashboard - Current user:', userData);
      
      if (!userData) {
        console.log('No user data, redirecting to login');
        navigate('/login');
        return;
      }

      // Check if user has admin privileges
      const userIsAdmin = isAdmin();
      console.log('User is admin:', userIsAdmin);
      
      if (!userIsAdmin) {
        console.log('User is not admin, redirecting to lobby');
        navigate('/lobby');
        return;
      }

      // Set user from localStorage immediately for display
      setUser(userData);
      
      // Then fetch updated data from backend
      await fetchAdminWallet();
      fetchCommissionWallet();
      
      setLoading(false);
    };

    checkAdminAccess();
  }, [currentUser, navigate, isAdmin]);

  // If the active tab isn't permitted (e.g. after re-login as a limited admin),
  // jump to the first tab the user can access.
  useEffect(() => {
    if (!canAccessTab(activeTab)) {
      const firstAllowed = Object.keys(TAB_PERMISSIONS).find((tab) => canAccessTab(tab));
      if (firstAllowed) setActiveTab(firstAllowed);
    }
  }, [activeTab, currentUser, user]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const renderTabContent = () => {
    if (!canAccessTab(activeTab)) {
      return (
        <div className="access-denied">
          <i className="fas fa-lock"></i>
          <h3>Access Denied</h3>
          <p>You don't have permission to view this section.</p>
        </div>
      );
    }
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard />;
      case 'transactions':
        return <TransactionStatement onWalletUpdate={fetchAdminWallet} />;
      case 'users':
        return <UserManagement countryInfo={countryInfo} />; // Pass countryInfo
      case 'cash-injection':
        return <CashInjection 
          onWalletUpdate={fetchAdminWallet} 
          countryInfo={countryInfo} // Pass countryInfo to CashInjection
        />;
      case 'approvals':
        return <FundApprovals onWalletUpdate={fetchAdminWallet} />;
      case 'currency-conversion':
        return <CurrencyConversion 
          walletBalances={walletBalances} 
          onWalletUpdate={fetchAdminWallet}
          user={user}
          countryInfo={countryInfo}
        />;
      case 'promotions':
        return <Promotions />;
      case 'fair-play':
        return <FairPlay />;
      case 'test-users':
        return <CreateTestUser endpoint="/api/admin/create-test-user" defaultCountryCode={countryInfo.country_code} />;
      default:
        return <TransactionStatement onWalletUpdate={fetchAdminWallet} />;
    }
  };

  // FIXED: Proper currency symbols for each currency
  const formatCurrency = (value, currencyCode = 'USD') => {
    const symbols = {
      'INR': '₹',
      'USD': '$',
      'LRD': 'L$',
      'XOF': 'CFA',
      'EUR': '€',
      'GBP': '£',
      'GHS': 'GH₵',
      'NGN': '₦'
    };
    
    // Use specific symbol for each currency
    const symbol = symbols[currencyCode] || currencyCode;
    const num = safeParseNumber(value);
    return `${symbol}${num.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading Admin Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <h1>🎯 JV Rummy Admin</h1>
          <span className="admin-badge">Administrator</span>
          {/* Show Admin Country Name */}
          {countryInfo.country_name && countryInfo.country_name !== 'Unknown' && (
            <div className="country-badge">
              <i className="fas fa-globe"></i>
              <span>{countryInfo.country_name}</span>
              {countryInfo.country_code && (
                <span className="country-code">({countryInfo.country_code})</span>
              )}
            </div>
          )}
        </div>
        
        <div className="admin-header-right">
          <div className="admin-user-info">
            <div className="user-details">
              <span className="user-name">Welcome, {user?.username || user?.email}</span>
              <span className="user-role">{user?.role}</span>
            </div>
            
            <div className="wallet-display">
              <div className="wallet-item">
                <span className="wallet-label">USD:</span>
                <span className="wallet-amount usd">
                  {formatCurrency(walletBalances.usd, 'USD')}
                </span>
                <div className="currency-tooltip">US Dollar</div>
              </div>
              <div className="wallet-item commission">
                <span className="wallet-label">Commission:</span>
                <span className="wallet-amount usd">
                  {formatCurrency(commissionBalance, 'USD')}
                </span>
                <div className="currency-tooltip">Commission earned (USD)</div>
              </div>
              <div className="wallet-item">
                <span className="wallet-label">INR:</span>
                <span className="wallet-amount inr">
                  {formatCurrency(walletBalances.inr, 'INR')}
                </span>
                <div className="currency-tooltip">Indian Rupee</div>
              </div>
              <div className="wallet-item">
                <span className="wallet-label">{countryInfo.local_currency_code}:</span>
                <span className="wallet-amount local">
                  {formatCurrency(walletBalances.local, countryInfo.local_currency_code)}
                </span>
                <div className="currency-tooltip">
                  {countryInfo.local_currency_name} - {countryInfo.country_name}
                </div>
              </div>
            </div>
          </div>
          
          <div className="header-actions">
            <button
              onClick={() => setShowChangePassword(true)}
              className="logout-btn"
              style={{ background: '#0e6b45' }}
            >
              <i className="fas fa-key"></i>
              Change Password
            </button>
            <button onClick={handleLogout} className="logout-btn">
              <i className="fas fa-sign-out-alt"></i>
              Logout
            </button>
          </div>
        </div>
      </header>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}

      {/* Main Content */}
      <div className="admin-container">
        {/* Sidebar Navigation */}
        <nav className="admin-sidebar">
          <div className="sidebar-menu">
            {canAccessTab('dashboard') && (
              <div 
                className={`menu-item ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => setActiveTab('dashboard')}
              >
                <i className="fas fa-chart-line"></i>
                <span>Dashboard</span>
              </div>
            )}

            {canAccessTab('transactions') && (
              <div 
                className={`menu-item ${activeTab === 'transactions' ? 'active' : ''}`}
                onClick={() => setActiveTab('transactions')}
              >
                <i className="fas fa-file-invoice-dollar"></i>
                <span>Transaction Statement</span>
              </div>
            )}
            
            {canAccessTab('users') && (
              <div 
                className={`menu-item ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                <i className="fas fa-users"></i>
                <span>User Management</span>
              </div>
            )}

            {canAccessTab('cash-injection') && (
              <div 
                className={`menu-item ${activeTab === 'cash-injection' ? 'active' : ''}`}
                onClick={() => setActiveTab('cash-injection')}
              >
                <i className="fas fa-money-bill-wave"></i>
                <span>Cash Injection</span>
              </div>
            )}

            {canAccessTab('approvals') && (
              <div 
                className={`menu-item ${activeTab === 'approvals' ? 'active' : ''}`}
                onClick={() => setActiveTab('approvals')}
              >
                <i className="fas fa-clipboard-check"></i>
                <span>Fund Approvals</span>
              </div>
            )}

            {/* New Currency Conversion Tab */}
            {canAccessTab('currency-conversion') && (
              <div 
                className={`menu-item ${activeTab === 'currency-conversion' ? 'active' : ''}`}
                onClick={() => setActiveTab('currency-conversion')}
              >
                <i className="fas fa-exchange-alt"></i>
                <span>Currency Conversion</span>
              </div>
            )}

            {/* Promotions Tab */}
            {canAccessTab('promotions') && (
              <div 
                className={`menu-item ${activeTab === 'promotions' ? 'active' : ''}`}
                onClick={() => setActiveTab('promotions')}
              >
                <i className="fas fa-gift"></i>
                <span>Promotions</span>
              </div>
            )}

            {canAccessTab('fair-play') && (
              <div 
                className={`menu-item ${activeTab === 'fair-play' ? 'active' : ''}`}
                onClick={() => setActiveTab('fair-play')}
              >
                <i className="fas fa-shield-alt"></i>
                <span>Fair Play</span>
              </div>
            )}

            {canAccessTab('test-users') && canCreateTestUsers() && (
              <div 
                className={`menu-item ${activeTab === 'test-users' ? 'active' : ''}`}
                onClick={() => setActiveTab('test-users')}
              >
                <i className="fas fa-user-plus"></i>
                <span>Create Test User</span>
              </div>
            )}
            
            <div className="menu-item system-health">
              <i className="fas fa-heartbeat"></i>
              <span>System Status: <span className="status-online">Online</span></span>
            </div>
          </div>
        </nav>

        {/* Main Content Area */}
        <main className="admin-main">
          <div className="admin-content">
            {renderTabContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;